import { IconButton, Scrollbar, SingleSelectDropdown, Spinner } from "@carbon6/c6-frontend-design";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { listMyWorkspaces } from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import { ResourceBrowserLayout } from "@/components/browse";
import {
  FilterBar,
  FilterResultCount,
  FilterSearchInput,
  type FilterBarChip,
} from "@/components/filter";
import {
  GPU_USAGE_PERIOD_OPTIONS,
  GpuUsageStrip,
  gpuUsagePeriodLabel,
  useGpuClusterUsage,
  type GpuUsagePeriod,
} from "@/components/gpu/usage";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { createLogger } from "@/logging";
import { createGpuQuotaColumns } from "./gpuQuotaColumns";
import {
  DEFAULT_GPU_QUOTA_USAGE_PERIOD,
  EMPTY_GPU_QUOTA_FILTERS,
  readGpuQuotaFilters,
  writeGpuQuotaFilters,
  type GpuQuotaFilterState,
} from "./gpuQuotaFilters";
import { GpuQuotaRequestModal, type GpuQuotaRequestTarget } from "./GpuQuotaRequestModal";
import { GpuQuotaTableSource } from "./GpuQuotaTableSource";
import { GpuQuotaWorkspaceRow } from "./GpuQuotaTableRow";
import { GpuQuotaWorkspaceChild } from "./GpuQuotaWorkspaceChild";
import styles from "./GpuQuotaPage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const logger = createLogger("page/gpuQuota");

const SUMMARY_LABEL = "선점자";

// 쿼터 탭. 위에는 기간 사용량 요약 띠(타일과 일별 차트)가 있고, 아래에는 사람별 표가 있다.
// 띠와 표는 같은 기간을 쓴다. 클러스터 점유 추세는 표와 따로 읽는다. 그래서 그 조회가 실패해도
// 표는 그린다.
//
// 사람(선점 요약) 행을 펼치면 그 사람이 가진 workspace 내역이 행 바로 아래 하위
// 행(c6 renderRowChild)으로 열린다. 하위 행 수치는 표 컬럼 트랙에 정렬된다. 그래서 사람 행 수치와
// 세로로 나란히 보인다. 한도 요청 대상은 workspace 하나다. 그래서 요청 버튼도 내역 한 건에 있고,
// 대상을 따로 고를 필요가 없다.
//
// 조회 축(기간, 검색)은 URL 이 소유한다(rules.md §17). 이 축들은 이 탭에만 적용되므로 필터 바는
// 페이지 belowTabs 가 아니라 이 패널의 ResourceBrowserLayout filters 슬롯에 둔다.
//
// 요청 버튼은 그 workspace 를 선점한 본인이거나 admin 일 때만 그린다. 쿼터 응답에는 선점 여부가
// 없다. 그래서 내 workspace 목록을 따로 읽어 대조한다. 접수 권한은 서버가 다시 판정하므로 이
// 판정은 표시용이다. 목적은 버튼을 눌러야 403 을 알게 되는 UX 를 피하는 것이다.
export function GpuQuotaTablePanel({
  canRequestAnyWorkspace,
}: {
  // admin 은 선점 없이도 접수할 수 있다.
  canRequestAnyWorkspace: boolean;
}): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readGpuQuotaFilters(searchParams), [searchParams]);
  const { period, query } = filters;

  const [source] = useState(
    () => new GpuQuotaTableSource({ initialPeriod: period, initialQuery: query }),
  );
  const [myWorkspaceIds, setMyWorkspaceIds] = useState<ReadonlySet<string>>(() => new Set());
  const [requestTarget, setRequestTarget] = useState<GpuQuotaRequestTarget | null>(null);
  // 새로고침은 표와 띠를 함께 다시 읽는다. 띠의 조회는 이 값이 바뀌면 다시 실행된다.
  const [reloadToken, setReloadToken] = useState(0);
  const { state: cluster, isLoading: isClusterLoading } = useGpuClusterUsage(period, reloadToken);

  useEffect(() => {
    void source.initialize();
    return () => {
      source.dispose();
    };
  }, [source]);

  // URL 조건이 바뀌면(드롭다운, 검색, 칩, 뒤로가기) 소스에 전달한다. setter 는 같은 값이면 no-op
  // 이다. 그래서 첫 마운트에서는 다시 조회하지 않는다. 생성 옵션이 이미 그 값을 가진다.
  useEffect(() => {
    void source.setPeriod(period);
  }, [source, period]);
  useEffect(() => {
    source.setQuery(query);
  }, [source, query]);

  // 내가 선점한 workspace. 로드가 실패해도 표는 그린다. 요청 버튼만 감춘다.
  useEffect(() => {
    let disposed = false;
    void listMyWorkspaces()
      .then((workspaces) => {
        if (!disposed) {
          setMyWorkspaceIds(new Set(workspaces.map((workspace) => workspace.id)));
        }
      })
      .catch((error: unknown) => {
        logger.error("Failed to load claimed workspaces for quota request gating.", error);
      });
    return () => {
      disposed = true;
    };
  }, []);

  const handleRefresh = useCallback(() => {
    setReloadToken((token) => token + 1);
    void source.refresh();
  }, [source]);

  const handleFiltersChange = useCallback(
    (next: GpuQuotaFilterState) => {
      setSearchParams((prev) => writeGpuQuotaFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const isLoading = snapshot.loadState === "loading" || isClusterLoading;
  // 표의 행 수에는 미할당 행과 꼬리 행이 포함된다. 결과 수는 선점자만 센다.
  const memberCount = source.getMemberCount();
  const maxGpuHours = source.getMaxGpuHours();

  const columns = useMemo(
    () =>
      createGpuQuotaColumns({
        onToggle: (row) => source.toggleMember(row.getRowId()),
        maxGpuHours,
      }),
    [source, maxGpuHours],
  );

  const canRequestWorkspace = useCallback(
    (workspace: GpuQuotaWorkspaceRow) =>
      canRequestAnyWorkspace || myWorkspaceIds.has(workspace.workspaceId),
    [canRequestAnyWorkspace, myWorkspaceIds],
  );

  // 기간은 항상 적용된다. 그래서 기본값이 아닐 때만 칩으로 표시한다. 검색어는 입력 칸이 이미
  // 보여 주므로 칩을 만들지 않는다.
  const chips: FilterBarChip[] =
    period === DEFAULT_GPU_QUOTA_USAGE_PERIOD
      ? []
      : [
          {
            key: "period",
            label: `기간: ${gpuUsagePeriodLabel(period)}`,
            removeLabel: "기간을 최근 한 달로 되돌리기",
            onRemove: () =>
              handleFiltersChange({ ...filters, period: DEFAULT_GPU_QUOTA_USAGE_PERIOD }),
          },
        ];

  return (
    <Scrollbar className={styles.quotaTab} gutter="auto" data-route-scroll-container="true">
      <GpuUsageStrip usage={source.getUsage()} cluster={cluster} onRetry={handleRefresh} />
      <ResourceBrowserLayout
        ariaLabel="GPU Quota 목록"
        summaryCount={memberCount}
        summaryLabel={SUMMARY_LABEL}
        summaryInFilter
        filters={
          <FilterBar
            ariaLabel="GPU Quota 필터"
            search={
              <FilterSearchInput
                value={query}
                onCommit={(next) => handleFiltersChange({ ...filters, query: next })}
                ariaLabel="대상 검색"
                placeholder="선점자 또는 workspace 이름"
              />
            }
            chips={chips}
            onClearAll={() => handleFiltersChange(EMPTY_GPU_QUOTA_FILTERS)}
          >
            <SingleSelectDropdown
              mode={hatcheryDropdownModes.filter}
              label="기간"
              neutralValue={DEFAULT_GPU_QUOTA_USAGE_PERIOD}
              value={period}
              options={[...GPU_USAGE_PERIOD_OPTIONS]}
              onValueChange={(next) =>
                handleFiltersChange({ ...filters, period: next as GpuUsagePeriod })
              }
            />
          </FilterBar>
        }
        error={tableStripError(snapshot)}
        onRetry={handleRefresh}
      >
        {/* 목록 상단 띠. 결과 수(idle)와 새로고침을 표시한다. 행 선택이 없는 목록이라 액션은 없다. */}
        <div className={bulkActionBarStyles.host}>
          <BulkActionBar
            source={source}
            idleContent={<FilterResultCount count={memberCount} unitLabel={SUMMARY_LABEL} />}
            rightContent={
              <IconButton
                text={isLoading ? "불러오는 중…" : "새로고침"}
                size="sm"
                variant="surface"
                disabled={isLoading}
                onClick={handleRefresh}
              >
                {isLoading ? <Spinner size="sm" /> : null}
              </IconButton>
            }
            ariaLabel="GPU Quota 목록 도구"
          />
          <HatDataTable
            source={source}
            columns={columns}
            emptyMessage={
              query === ""
                ? "표시할 workspace 가 없습니다."
                : "검색어와 맞는 대상이 없습니다. 다른 이름으로 검색해 보세요."
            }
            loadingMessage="GPU Quota 를 불러오고 있습니다."
            ariaLabel="GPU Quota 테이블"
            showIndex={false}
            scrollable
            minWidth="1180px"
            renderRowChild={(child) =>
              child instanceof GpuQuotaWorkspaceRow ? (
                <GpuQuotaWorkspaceChild
                  workspace={child}
                  maxGpuHours={maxGpuHours}
                  canRequest={canRequestWorkspace(child)}
                  onRequest={() => setRequestTarget(toRequestTarget(child))}
                />
              ) : null
            }
          />
        </div>
      </ResourceBrowserLayout>
      <GpuQuotaRequestModal
        target={requestTarget}
        onClose={() => setRequestTarget(null)}
        // 접수만으로는 한도가 바뀌지 않는다(admin 이 승인해야 올라간다). 하지만 점유는 바뀔 수 있다.
        onSubmitted={() => void source.refresh()}
      />
    </Scrollbar>
  );
}

function toRequestTarget(workspace: GpuQuotaWorkspaceRow): GpuQuotaRequestTarget {
  return {
    workspaceId: workspace.workspaceId,
    workspaceName: workspace.displayName,
    effectiveGpuLimit: workspace.effectiveGpuLimit,
    gpuLimit: workspace.gpuLimit,
    groupGpuMaxLimit: workspace.groupGpuMaxLimit,
  };
}
